import { useIsFocused } from '@react-navigation/native'
import qs from 'qs'
import { useEffect } from 'react'
import useSWR, { SWRConfiguration } from 'swr'

import { ConsultationStatus, ConsultationType } from '../../@types'
import { useAuth } from '../../context/auth'
import fetcher from '../../lib/fetcher'
import { getConsultationStartTime } from '../../util/consultation'
import { getAllEligibleFamilyProfilesIds } from '../../util/profile'
import { NO_REFRESH } from '../use-get-document'
import { TWO_SECONDS } from '../use-patient-current-consultation'

import { ConsultationResponse, ConsultationsResponse } from './types'

type Props = {
  status: ConsultationStatus | ConsultationStatus[]
  type?: ConsultationType
  sortBy?: string
  page?: number
  pageSize?: number
}

export function useConsultations(
  { status, type, page, pageSize, sortBy = 'createdAt:desc' }: Props,
  configuration: SWRConfiguration = {}
) {
  const { profile } = useAuth()
  const patientIds = getAllEligibleFamilyProfilesIds(profile)
  const hasPatientIds = !!patientIds?.length

  const queries = patientIds?.map(id => {
    const query = qs.stringify({ fields: '*', status, type, page, pageSize, sortBy }, { indices: false })
    return `/patients/${id}/consultations?${query}`
  })

  const data = useSWR<ConsultationsResponse[]>(hasPatientIds ? [queries, { auth: true }] : null, fetcher.apiAll, {
    revalidateOnFocus: true,
    revalidateIfStale: true,
    revalidateOnReconnect: false,
    refreshWhenOffline: false,
    refreshWhenHidden: false,
    refreshInterval: TWO_SECONDS,
    ...configuration,
  })

  useEffect(() => {
    if (data.error) {
      console.warn(`🔴 Error getting consultations`, data.error)
    }
  }, [data.error])

  const groupedResults = data.data?.reduce(
    (acc, result) => {
      acc.consultations = [...acc.consultations, ...result.consultations].sort(
        (a, b) => new Date(getConsultationStartTime(a)).getTime() - new Date(getConsultationStartTime(b)).getTime()
      )
      const totalConsultations = acc.consultations.length
      acc.pagination = {
        ...result.pagination,
        total: totalConsultations,
        pageCount: Math.ceil(totalConsultations / result.pagination.pageSize),
      }
      return acc
    },
    { consultations: [] as ConsultationResponse[], pagination: { total: 0, page: 0, pageCount: 0, pageSize: 0 } }
  )

  return { data: groupedResults, error: data.error, isLoading: !data.error && !data.data, refreshAll: data.mutate }
}

export function useConsultation(id: number) {
  const { profile } = useAuth()

  const { data, error } = useSWR<ConsultationResponse>(
    profile && id ? [`/patients/${profile.id}/consultations/${id}`, { auth: true }] : null,
    fetcher.api
  )

  useEffect(() => {
    if (error) {
      console.warn(`🔴 Error getting consultation with id ${id}`, error)
    }
  }, [error])

  return { data, error }
}

const FIFTEEN_SECONDS = 15 * 1000

export function useActiveConsultations() {
  const isFocused = useIsFocused()

  const { data, error, isLoading, refreshAll } = useConsultations(
    {
      status: [ConsultationStatus.QUEUED, ConsultationStatus.ACTIVE, ConsultationStatus.PENDING],
    },
    {
      revalidateOnFocus: isFocused,
      revalidateOnReconnect: isFocused,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: isFocused ? FIFTEEN_SECONDS : NO_REFRESH,
    }
  )

  return {
    data,
    error,
    hasActiveConsultations: !!data?.consultations?.length,
    isLoading,
    refreshAll,
  }
}

export function useClosedConsultations() {
  const { data, error, isLoading, refreshAll } = useConsultations({
    status: [ConsultationStatus.CLOSED, ConsultationStatus.CLOSED_WITH_ERROR],
  })

  return {
    data,
    error,
    isLoading,
    refreshAll,
  }
}

export function useOnDemandConsultations() {
  const { data, error, isLoading, refreshAll } = useConsultations({
    status: [ConsultationStatus.ACTIVE, ConsultationStatus.QUEUED],
    type: ConsultationType.ON_DEMAND,
  })

  return {
    data,
    error,
    ongoingOnDemandConsultation: data?.consultations[0],
    hasOngoingOnDemandConsultations: !!data?.consultations?.length,
    isLoading,
    refreshAll,
  }
}
